import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useStudent, useStudentAuditLogs, useUpdateStudentStatus } from '../lib/hooks';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, 
  Loader2, 
  Mail, 
  Phone, 
  MessageCircle,
  Pencil,
  UserX,
  UserCheck,
  Calendar,
  MapPin,
  Globe,
  User,
  FileText,
  BookOpen,
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Handshake
} from 'lucide-react';
import StudentForm from '../components/students/StudentForm';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../lib/translations';

const levelColors = {
  debutant: 'bg-slate-100 text-slate-700',
  A1: 'bg-slate-200 text-slate-800',
  A2: 'bg-blue-100 text-blue-700',
  B1: 'bg-yellow-100 text-yellow-700',
  B2: 'bg-orange-100 text-orange-700',
  C1: 'bg-green-100 text-green-700',
  C2: 'bg-emerald-100 text-emerald-700',
};

const statusColors = {
  pending: 'bg-slate-100 text-slate-600',
  active: 'bg-success/10 text-success',
  completed: 'bg-blue-100 text-blue-700',
  suspended: 'bg-destructive/10 text-destructive',
};

const statusLabels = {
  pending: 'En attente',
  active: 'Actif',
  completed: 'Terminé',
  suspended: 'Suspendu',
};

const visaLabels = {
  studies: 'Études',
  professional: 'Formation pro',
  work: 'Travail',
  family: 'Regroupement familial',
};

const actionIcons = {
  created: CheckCircle,
  updated: RefreshCw,
  status_changed: AlertCircle,
  deleted: XCircle,
};

const StudentDetailPage = () => {
  const { id } = useParams();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formOpen, setFormOpen] = useState(false);
  
  // RBAC permissions
  const isAdmin = user?.role === 'admin';
  const isRespPeda = user?.role === 'resp_peda';
  const isRespAdmin = user?.role === 'resp_admin';
  const canEdit = isAdmin || isRespPeda || isRespAdmin;
  const canSuspend = isAdmin; // Only admin can suspend/activate
  const canViewAuditLogs = isAdmin; // Only admin can view activity logs
  
  const { data: student, isLoading, error } = useStudent(id);
  const { data: auditLogs } = useStudentAuditLogs(id, { enabled: canViewAuditLogs });
  const updateStatus = useUpdateStudentStatus(id);

  const handleStatusToggle = async () => {
    if (!student) return;
    const newStatus = student.enrollment_status === 'suspended' ? 'active' : 'suspended';
    try {
      await updateStatus.mutateAsync(newStatus);
      toast.success(`Étudiant ${newStatus === 'suspended' ? 'suspendu' : 'activé'}`);
    } catch (error) {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">Étudiant non trouvé</h2>
        <Button variant="outline" onClick={() => navigate('/students')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à la liste
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="student-detail-page">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate('/students')} className="mb-2">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour aux étudiants
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Profile */}
        <div className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              {/* Avatar and name */}
              <div className="text-center mb-6">
                <div className="w-24 h-24 rounded-full bg-primary mx-auto flex items-center justify-center text-white text-3xl font-bold mb-4">
                  {student.first_name[0]}{student.last_name[0]}
                </div>
                <h1 className="font-heading text-2xl font-bold">
                  {student.first_name} {student.last_name}
                </h1>
              </div>

              {/* Contact info */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm truncate">{student.email}</span>
                </div>
                {student.phone && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Phone className="w-4 h-4" />
                    <span className="text-sm">{student.phone}</span>
                  </div>
                )}
                {student.whatsapp && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <MessageCircle className="w-4 h-4" />
                    <span className="text-sm">{student.whatsapp}</span>
                  </div>
                )}
              </div>

              <Separator className="my-4" />

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge className={levelColors[student.german_level] || levelColors.A1}>
                  Niveau {student.german_level || 'A1'}
                </Badge>
                <Badge className={statusColors[student.enrollment_status]}>
                  {statusLabels[student.enrollment_status]}
                </Badge>
                <Badge variant="outline">
                  {visaLabels[student.visa_objective] || student.visa_objective}
                </Badge>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                {canEdit && (
                  <Button 
                    className="w-full" 
                    onClick={() => setFormOpen(true)}
                    data-testid="edit-student-btn"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Modifier
                  </Button>
                )}
                {canSuspend && (
                  <Button 
                    variant={student.enrollment_status === 'suspended' ? 'default' : 'destructive'}
                    className="w-full"
                    onClick={handleStatusToggle}
                    disabled={updateStatus.isPending}
                    data-testid="toggle-status-btn"
                  >
                    {student.enrollment_status === 'suspended' ? (
                      <>
                        <UserCheck className="w-4 h-4 mr-2" />
                        Activer
                      </>
                    ) : (
                      <>
                        <UserX className="w-4 h-4 mr-2" />
                        Suspendre
                      </>
                    )}
                  </Button>
                )}
              </div>

              <Separator className="my-4" />

              {/* Partner */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Handshake className="w-4 h-4" />
                <span>
                  {student.partner_id 
                    ? 'Partenaire assigné' 
                    : 'Aucun partenaire assigné'
                  }
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Tabs */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="informations" className="space-y-6">
            <TabsList className="w-full justify-start bg-muted/50 p-1">
              <TabsTrigger value="informations">Informations</TabsTrigger>
              <TabsTrigger value="progression">Progression</TabsTrigger>
              <TabsTrigger value="dossier">{lang === 'de' ? 'Akte' : 'Dossier'}</TabsTrigger>
              <TabsTrigger value="paiements">{t('payments_title', lang)}</TabsTrigger>
              {canViewAuditLogs && <TabsTrigger value="activite">Activité</TabsTrigger>}
            </TabsList>

            {/* Tab: Informations */}
            <TabsContent value="informations" className="space-y-6">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <User className="w-5 h-5 text-primary" />
                    Informations personnelles
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('students_nationality', lang)}</p>
                    <p className="font-medium">{student.nationality || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('students_dob', lang)}</p>
                    <p className="font-medium">
                      {student.date_of_birth ? formatDate(student.date_of_birth) : '-'}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground">{t('students_address', lang)}</p>
                    <p className="font-medium">{student.address || '-'}</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Notes
                  </CardTitle>
                  <CardDescription>Notes internes (visibles uniquement par l'administration)</CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea 
                    value={student.notes || ''} 
                    placeholder="Aucune note"
                    className="min-h-[100px]"
                    readOnly
                  />
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary" />
                    Historique d'inscription
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Inscrit le {formatDate(student.created_at)}
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Progression */}
            <TabsContent value="progression">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-secondary" />
                    Module Cours
                  </CardTitle>
                  <CardDescription>Disponible prochainement</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <Badge className={levelColors[student.german_level] || levelColors.A1}>
                        Niveau actuel: {student.german_level || 'A1'}
                      </Badge>
                      {student.assigned_level && (
                        <Badge variant="outline">
                          Niveau assigné: {student.assigned_level}
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Progression vers C1</span>
                        <span className="font-medium">--</span>
                      </div>
                      <Progress value={0} className="h-2" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Dossier */}
            <TabsContent value="dossier">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-accent" />
                    Module Dossiers
                  </CardTitle>
                  <CardDescription>Disponible prochainement</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <Badge variant="outline">
                      Objectif: {visaLabels[student.visa_objective] || student.visa_objective}
                    </Badge>
                    <div className="flex items-center gap-2">
                      <Progress 
                        value={student.documents_count > 0 ? (student.documents_validated / student.documents_count) * 100 : 0} 
                        className="h-2 flex-1" 
                      />
                      <span className="text-sm text-muted-foreground">
                        {student.documents_validated}/{student.documents_count || 0} documents
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Paiements */}
            <TabsContent value="paiements">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-warning" />
                    Module Paiements
                  </CardTitle>
                  <CardDescription>Disponible prochainement</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Le suivi des paiements sera disponible dans une prochaine mise à jour.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Activité - Only visible to admin */}
            {canViewAuditLogs && (
              <TabsContent value="activite">
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5 text-primary" />
                      Historique d'activité
                    </CardTitle>
                    <CardDescription>Les 20 dernières actions sur ce profil</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {!auditLogs || auditLogs.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        Aucune activité enregistrée
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {auditLogs.map((log) => {
                          const Icon = actionIcons[log.action] || Clock;
                          return (
                            <div key={log.id} className="flex gap-4 pb-4 border-b last:border-0">
                              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                                <Icon className="w-4 h-4 text-muted-foreground" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium">{log.details}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Par {log.actor_name} • {formatDateTime(log.created_at)}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>

      {/* Edit Form Sheet */}
      <StudentForm 
        open={formOpen} 
        onClose={() => setFormOpen(false)} 
        student={student} 
      />
    </div>
  );
};

export default StudentDetailPage;
